import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, Slider, TextField, InputAdornment } from '@mui/material';
import { useFilterContext } from './filtercontext';
import { LocalConvenienceStoreOutlined } from '@mui/icons-material';
import { formatCurrency } from '../../../utils/formatters';

export default function CurrencyRangeFilter({
  field,
  label,
  getNumericValue = item => Number(item[field]),
  minLabel = "Min",
  maxLabel = "Max",
  unit = ""
}) {
  const { originalData, registerFilter, clearFilter } = useFilterContext();

  // States for data range and selected range
  const [dataMin, setDataMin] = useState(0);
  const [dataMax, setDataMax] = useState(100);
  const [range, setRange] = useState([0, 100]);
  const [minInputValue, setMinInputValue] = useState('0.00');
  const [maxInputValue, setMaxInputValue] = useState('100.00');

  // Track if we've initialized the range from data
  const [initialized, setInitialized] = useState(false);

  // Use ref to prevent unnecessary re-registrations and cache original data
  const currentRangeRef = useRef([0, 100]);
  const hasFilterRef = useRef(false);
  const originalDataCacheRef = useRef(null);
  const cachedRangeRef = useRef({ min: 0, max: 100 });

  // Helper function to round to 2 decimal places
  const roundToTwoDecimals = (value) => {
    return Math.round(value * 100) / 100;
  };

  // Helper function to parse currency string back to number
  const parseCurrencyString = (currencyStr) => {
    if (!currencyStr || currencyStr === '') return 0;

    // Remove formatting characters and handle negative values in parentheses
    const cleanStr = currencyStr
      .replace(/[$,\s]/g, '') // Remove $, commas, and spaces
      .replace(/[()]/g, ''); // Remove parentheses

    const isNegative = currencyStr.includes('(') && currencyStr.includes(')');
    const number = parseFloat(cleanStr);

    return isNaN(number) ? 0 : (isNegative ? -number : number);
  };

  // Calculate and update range when originalData changes (only when truly new data comes in)
  useEffect(() => {
    // Only recalculate if originalData reference actually changed or we haven't initialized
    if (originalDataCacheRef.current === originalData && initialized) {
      return;
    }

    originalDataCacheRef.current = originalData;

    if (!originalData || !originalData.length) {
      if (!initialized) {
        const defaultRange = { min: 0, max: 100 };
        setDataMin(defaultRange.min);
        setDataMax(defaultRange.max);
        setRange([defaultRange.min, defaultRange.max]);
        setMinInputValue(formatCurrency(defaultRange.min));
        setMaxInputValue(formatCurrency(defaultRange.max));
        cachedRangeRef.current = defaultRange;
        setInitialized(true);
      }
      return;
    }

    const values = originalData
      .map(getNumericValue)
      .filter(value => !isNaN(value) && value !== null);

    if (!values.length) {
      if (!initialized) {
        const defaultRange = { min: 0, max: 100 };
        setDataMin(defaultRange.min);
        setDataMax(defaultRange.max);
        setRange([defaultRange.min, defaultRange.max]);
        setMinInputValue(formatCurrency(defaultRange.min));
        setMaxInputValue(formatCurrency(defaultRange.max));
        cachedRangeRef.current = defaultRange;
        setInitialized(true);
      }
      return;
    }

    const newDataMin = roundToTwoDecimals(Math.min(...values));
    const newDataMax = roundToTwoDecimals(Math.max(...values));

    // Only update if values actually changed or not initialized
    if (newDataMin !== cachedRangeRef.current.min || newDataMax !== cachedRangeRef.current.max || !initialized) {
      setDataMin(newDataMin);
      setDataMax(newDataMax);
      setRange([newDataMin, newDataMax]);
      setMinInputValue(formatCurrency(newDataMin));
      setMaxInputValue(formatCurrency(newDataMax));
      currentRangeRef.current = [newDataMin, newDataMax];
      cachedRangeRef.current = { min: newDataMin, max: newDataMax };
      setInitialized(true);
    }
  }, [originalData, getNumericValue, initialized]);

  // Create clear handler function
  const handleClearFromContext = () => {
    const cachedMin = cachedRangeRef.current.min;
    const cachedMax = cachedRangeRef.current.max;
    setRange([cachedMin, cachedMax]);
    setMinInputValue(formatCurrency(cachedMin));
    setMaxInputValue(formatCurrency(cachedMax));
    currentRangeRef.current = [cachedMin, cachedMax];
    hasFilterRef.current = false;
  };



  // Update filter when range changes
  useEffect(() => {
    if (!initialized) return;

    const shouldHaveFilter = range[0] > dataMin || range[1] < dataMax;

    // Check if the range actually changed
    const rangeChanged = range[0] !== currentRangeRef.current[0] || range[1] !== currentRangeRef.current[1];

    if (shouldHaveFilter && (rangeChanged || !hasFilterRef.current)) {
      const filterFn = item => {
        // console.log(`applying filter to item:`,item);
        const value = getNumericValue(item);
        // console.log(`Evaluating filter for field "${field}" with value ${value}`);
        // console.log(`Current range is [${range[0]}, ${range[1]}]`);
        if (isNaN(value) || value === null) return false;
        // console.log(`Value ${value} is ${value >= range[0] && value <= range[1] ? 'within' : 'outside'} the range`);
        return value >= range[0] && value <= range[1];
      };

      const handleFilterDescription = () => {
          return `${label}: ${unit}${formatCurrency(range[0])} - ${unit}${formatCurrency(range[1])}`;
      }

      // console.log(`Registering filter for field "${field}" with range [${range[0]}, ${range[1]}]`);

      registerFilter(field, label, filterFn, handleClearFromContext, handleFilterDescription);
      hasFilterRef.current = true;
      currentRangeRef.current = [range[0], range[1]];
    } else if (!shouldHaveFilter && hasFilterRef.current) {
      clearFilter(field);
      hasFilterRef.current = false;
      currentRangeRef.current = [range[0], range[1]];
    }
  }, [range, field, dataMin, dataMax, getNumericValue, registerFilter, clearFilter, initialized]);

  // Handle slider change
  const handleSliderChange = (event, newValue) => {
    const roundedValue = [roundToTwoDecimals(newValue[0]), roundToTwoDecimals(newValue[1])];
    setRange(roundedValue);
    setMinInputValue(formatCurrency(roundedValue[0]));
    setMaxInputValue(formatCurrency(roundedValue[1]));
  };

  // Handle min input change
  const handleMinInputChange = (event) => {
    const inputValue = event.target.value;
    setMinInputValue(inputValue); // Keep the raw input for editing

    const numericValue = parseCurrencyString(inputValue);
    if (!isNaN(numericValue)) {
      const roundedValue = roundToTwoDecimals(numericValue);
      setRange([Math.min(roundedValue, range[1]), range[1]]);
    }
  };

  // Handle max input change
  const handleMaxInputChange = (event) => {
    const inputValue = event.target.value;
    setMaxInputValue(inputValue); // Keep the raw input for editing

    const numericValue = parseCurrencyString(inputValue);
    if (!isNaN(numericValue)) {
      const roundedValue = roundToTwoDecimals(numericValue);
      setRange([range[0], Math.max(roundedValue, range[0])]);
    }
  };

  // Handle input blur to format the display value
  const handleMinInputBlur = () => {
    const numericValue = parseCurrencyString(minInputValue);
    if (!isNaN(numericValue)) {
      setMinInputValue(formatCurrency(numericValue));
    }
  };

  const handleMaxInputBlur = () => {
    const numericValue = parseCurrencyString(maxInputValue);
    if (!isNaN(numericValue)) {
      setMaxInputValue(formatCurrency(numericValue));
    }
  };

  if (!initialized) {
    return (
      <Box>
        <Typography variant="subtitle2" gutterBottom>
          {label}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Loading...
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="subtitle2" gutterBottom>
        {label}
      </Typography>

      <Box sx={{ px: 1, pt: 2 }}>
        <Slider
          value={range}
          onChange={handleSliderChange}
          min={dataMin}
          max={dataMax}
          step={0.01}
          valueLabelDisplay="auto"
          valueLabelFormat={value => `${unit}${formatCurrency(value)}`}
        />
      </Box>

      <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
        <TextField
          label={minLabel}
          size="small"
          value={minInputValue}
          onChange={handleMinInputChange}
          onBlur={handleMinInputBlur}
          InputProps={{
            startAdornment: unit ? (
              <InputAdornment position="start">{unit}</InputAdornment>
            ) : null
          }}
        />

        <TextField
          label={maxLabel}
          size="small"
          value={maxInputValue}
          onChange={handleMaxInputChange}
          onBlur={handleMaxInputBlur}
          InputProps={{
            startAdornment: unit ? (
              <InputAdornment position="start">{unit}</InputAdornment>
            ) : null
          }}
        />
      </Box>
    </Box>
  );
}